import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Grid, Typography, Paper, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  IconButton, Tooltip, Badge,
} from "@mui/material";
import DirectionsBoatIcon   from "@mui/icons-material/DirectionsBoat";
import AnchorIcon           from "@mui/icons-material/Anchor";
import WavesIcon            from "@mui/icons-material/Waves";
import HourglassTopIcon     from "@mui/icons-material/HourglassTop";
import BuildIcon            from "@mui/icons-material/Build";
import CheckCircleIcon      from "@mui/icons-material/CheckCircle";
import WarningAmberIcon     from "@mui/icons-material/WarningAmber";
import AttachMoneyIcon      from "@mui/icons-material/AttachMoney";
import EventAvailableIcon   from "@mui/icons-material/EventAvailable";
import RefreshIcon          from "@mui/icons-material/Refresh";
import api from "../../utils/api";

/* ── helpers ── */
const fmtDate   = (d) => { if (!d) return "N/A"; return new Date(String(d).slice(0,10)+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}); };
const fmtMoney  = (n) => n == null ? "N/A" : `$${Number(n).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const daysUntil = (d) => { if (!d) return null; return Math.ceil((new Date(String(d).slice(0,10)+"T00:00:00") - new Date()) / 86400000); };

/* ── Occupancy ring ── */
function OccupancyRing({ pct, occupied, reserved, available, maintenance, total }) {
  const r = 54, circ = 2 * Math.PI * r;
  const segments = [
    { v: occupied,    color: "#ea580c" },
    { v: reserved,    color: "#2f63ad" },
    { v: maintenance, color: "#9ca3af" },
  ];
  let offset = 0;
  return (
    <Box sx={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
      <Box sx={{ position:"relative", width:130, height:130 }}>
        <svg width={130} height={130} style={{ transform:"rotate(-90deg)", display:"block" }}>
          <circle cx={65} cy={65} r={r} fill="none" stroke="#f0f4f8" strokeWidth={14} />
          {segments.map((seg, i) => {
            const pctSeg = total > 0 ? seg.v / total : 0;
            const dash = pctSeg * circ;
            const el = (
              <circle key={i} cx={65} cy={65} r={r} fill="none"
                stroke={seg.color} strokeWidth={14}
                strokeDasharray={`${dash} ${circ - dash}`}
                strokeDashoffset={-offset}
                style={{ transition:"stroke-dasharray 0.6s ease" }} />
            );
            offset += dash;
            return el;
          })}
        </svg>
        <Box sx={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", textAlign:"center" }}>
          <Typography variant="h4" sx={{ fontWeight:800, color:"#223142", lineHeight:1 }}>{pct}%</Typography>
          <Typography variant="caption" sx={{ color:"#6b778c", fontWeight:600, fontSize:"0.65rem" }}>Occupancy</Typography>
        </Box>
      </Box>
      <Box sx={{ display:"flex", gap:1.5, flexWrap:"wrap", justifyContent:"center", mt:1.5 }}>
        {[
          { label:`Occupied (${occupied})`,    color:"#ea580c" },
          { label:`Reserved (${reserved})`,    color:"#2f63ad" },
          { label:`Available (${available})`,  color:"#16a34a" },
          { label:`Maint. (${maintenance})`,   color:"#9ca3af" },
        ].map(s => (
          <Box key={s.label} sx={{ display:"flex", alignItems:"center", gap:0.5 }}>
            <Box sx={{ width:9, height:9, borderRadius:"50%", backgroundColor:s.color }} />
            <Typography variant="caption" sx={{ color:"#5f6b7a", fontSize:"0.65rem" }}>{s.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/* ── Clickable stat card ── */
function StatCard({ icon, label, value, color, bg, onClick, active, subtitle }) {
  return (
    <Paper elevation={0} onClick={onClick} sx={{
      p: 2, borderRadius:"16px", cursor: onClick ? "pointer" : "default",
      border: active ? `2px solid ${color}` : "1px solid rgba(47,99,173,0.08)",
      backgroundColor: active ? bg : "#fff",
      boxShadow: active ? `0 4px 16px ${color}28` : "0 2px 8px rgba(0,0,0,0.04)",
      transition:"all 0.18s ease",
      "&:hover": onClick ? { transform:"translateY(-2px)", boxShadow:`0 6px 20px ${color}30` } : {},
    }}>
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <Box>
          <Typography variant="caption" sx={{ color:"#6b778c", fontWeight:700, textTransform:"uppercase", letterSpacing:0.8, fontSize:"0.62rem" }}>{label}</Typography>
          <Typography variant="h4" sx={{ fontWeight:800, color:"#223142", lineHeight:1.1, mt:0.25, fontSize:"1.8rem" }}>{value ?? "N/A"}</Typography>
          {subtitle && <Typography variant="caption" sx={{ color:"#9aa5b4", fontSize:"0.65rem" }}>{subtitle}</Typography>}
        </Box>
        <Box sx={{ p:0.875, borderRadius:"12px", backgroundColor:bg }}>
          {React.cloneElement(icon, { sx:{ fontSize:20, color } })}
        </Box>
      </Box>
      {onClick && (
        <Typography variant="caption" sx={{ color, fontWeight:600, fontSize:"0.65rem", mt:0.75, display:"block" }}>
          {active ? "▲ Hide details" : "▼ View details"}
        </Typography>
      )}
    </Paper>
  );
}

/* ── Vessel detail table ── */
function VesselTable({ rows, emptyMsg }) {
  if (!rows?.length) return (
    <Box sx={{ py:4, textAlign:"center" }}>
      <DirectionsBoatIcon sx={{ fontSize:32, color:"#d0d7e0", mb:1, display:"block", mx:"auto" }} />
      <Typography variant="body2" sx={{ color:"#9aa5b4" }}>{emptyMsg}</Typography>
    </Box>
  );
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {["Slip","Vessel","LOA","Owner / Phone","Dates","Status","Total"].map(h => (
              <TableCell key={h} sx={{ fontWeight:700, fontSize:"0.7rem", backgroundColor:"#f8fafc", py:0.75, color:"#5f6b7a" }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => {
            const insDays = r.insurance_expiry   ? daysUntil(r.insurance_expiry)   : null;
            const regDays = r.registration_expiry ? daysUntil(r.registration_expiry) : null;
            const hasWarn = (insDays !== null && insDays <= 30) || (regDays !== null && regDays <= 30);
            return (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontWeight:700, fontSize:"0.78rem", whiteSpace:"nowrap" }}>
                  {r.dock_name} {r.slip_number}
                </TableCell>
                <TableCell>
                  <Box sx={{ display:"flex", alignItems:"center", gap:0.5 }}>
                    {hasWarn && <Tooltip title="Document expiry warning"><WarningAmberIcon sx={{ fontSize:13, color:"#d97706" }} /></Tooltip>}
                    <Typography variant="body2" sx={{ fontWeight:600, fontSize:"0.78rem" }}>{r.vessel_name}</Typography>
                  </Box>
                  {r.registration_number && <Typography variant="caption" sx={{ color:"#9aa5b4", fontFamily:"monospace", fontSize:"0.65rem" }}>{r.registration_number}</Typography>}
                </TableCell>
                <TableCell sx={{ fontSize:"0.75rem", color:"#5f6b7a" }}>{r.loa ? `${r.loa} ft` : "N/A"}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize:"0.75rem" }}>{r.owner_name || "N/A"}</Typography>
                  {r.owner_phone && <Typography variant="caption" sx={{ color:"#9aa5b4", fontFamily:"monospace", fontSize:"0.65rem" }}>{r.owner_phone}</Typography>}
                </TableCell>
                <TableCell sx={{ fontSize:"0.72rem", color:"#5f6b7a", whiteSpace:"nowrap" }}>
                  {fmtDate(r.start_date)}<br />→ {fmtDate(r.end_date)}
                </TableCell>
                <TableCell>
                  <Chip label={r.status?.replace("_"," ")} size="small" sx={{
                    fontSize:"0.62rem", height:18, fontWeight:700, textTransform:"capitalize",
                    backgroundColor: r.status==="checked_in" ? "rgba(22,163,74,0.12)" : "rgba(47,99,173,0.10)",
                    color: r.status==="checked_in" ? "#15803d" : "#1d4ed8",
                  }} />
                </TableCell>
                <TableCell sx={{ fontWeight:600, fontSize:"0.78rem", color:"#2f63ad", whiteSpace:"nowrap" }}>{fmtMoney(r.total_amount)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/* ── Expiry table ── */
function ExpiryTable({ rows }) {
  if (!rows?.length) return (
    <Box sx={{ py:4, textAlign:"center" }}>
      <CheckCircleIcon sx={{ fontSize:32, color:"#16a34a", mb:1, display:"block", mx:"auto" }} />
      <Typography variant="body2" sx={{ color:"#16a34a", fontWeight:600 }}>All vessel documents are current</Typography>
    </Box>
  );
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {["Vessel","Reg #","Owner / Phone","Insurance Expiry","Reg. Expiry","Alert"].map(h => (
              <TableCell key={h} sx={{ fontWeight:700, fontSize:"0.7rem", backgroundColor:"#f8fafc", py:0.75, color:"#5f6b7a" }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(v => {
            const insDays = daysUntil(v.insurance_expiry);
            const regDays = daysUntil(v.registration_expiry);
            const isExpired = (insDays !== null && insDays < 0) || (regDays !== null && regDays < 0);
            const ExpiryCell = ({ days, date }) => {
              if (days === null) return <Typography variant="caption" sx={{ color:"#9aa5b4" }}>-</Typography>;
              const expired = days < 0;
              const soon    = !expired && days <= 30;
              return (
                <Box>
                  <Typography variant="body2" sx={{ fontSize:"0.75rem", fontWeight:600, color: expired ? "#dc2626" : soon ? "#d97706" : "#374151" }}>
                    {fmtDate(date)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: expired ? "#dc2626" : soon ? "#d97706" : "#9aa5b4", fontSize:"0.62rem" }}>
                    {expired ? `${Math.abs(days)}d overdue` : `${days}d remaining`}
                  </Typography>
                </Box>
              );
            };
            return (
              <TableRow key={v.id} hover sx={{ backgroundColor: isExpired ? "rgba(220,38,38,0.025)" : undefined }}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight:700, fontSize:"0.78rem" }}>{v.vessel_name}</Typography>
                </TableCell>
                <TableCell sx={{ fontFamily:"monospace", fontSize:"0.72rem", color:"#5f6b7a" }}>{v.registration_number || "N/A"}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize:"0.75rem" }}>{v.owner_name || "N/A"}</Typography>
                  {v.owner_phone && <Typography variant="caption" sx={{ color:"#9aa5b4", fontSize:"0.65rem" }}>{v.owner_phone}</Typography>}
                </TableCell>
                <TableCell><ExpiryCell days={insDays} date={v.insurance_expiry} /></TableCell>
                <TableCell><ExpiryCell days={regDays} date={v.registration_expiry} /></TableCell>
                <TableCell>
                  <Chip
                    label={isExpired ? "EXPIRED" : "Expiring Soon"}
                    size="small"
                    sx={{
                      fontSize:"0.62rem", height:18, fontWeight:700,
                      backgroundColor: isExpired ? "rgba(220,38,38,0.12)" : "rgba(217,119,6,0.12)",
                      color: isExpired ? "#dc2626" : "#d97706",
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/* ════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════ */
const MarinaDashboard = ({ marinaId }) => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [panel,   setPanel]   = useState(null); // "arrivals"|"departures"|"checked_in"|"expiry"

  const load = useCallback(async () => {
    if (!marinaId) return;
    setLoading(true);
    try {
      const res = await api.get(`/marina-dashboard/${marinaId}`);
      setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [marinaId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setPanel(p => p === key ? null : key);

  if (loading) return (
    <Box sx={{ display:"flex", justifyContent:"center", py:6 }}>
      <CircularProgress />
    </Box>
  );
  if (!data) return <Alert severity="error" sx={{ borderRadius:"12px" }}>Failed to load dashboard data</Alert>;

  const { stats, arrivals, departures, checked_in, expiry_list } = data;
  const totalAlerts = (stats.insurance_expired||0) + (stats.registration_expired||0)
                    + (stats.insurance_expiring_soon||0) + (stats.registration_expiring_soon||0);

  const panelConfig = {
    arrivals:   { title:`Arriving Today (${arrivals?.length||0})`,        color:"#0891b2", bg:"rgba(8,145,178,0.04)"   },
    departures: { title:`Departing Today (${departures?.length||0})`,     color:"#7c3aed", bg:"rgba(124,58,237,0.04)" },
    checked_in: { title:`Currently On-Site (${checked_in?.length||0})`,   color:"#ea580c", bg:"rgba(234,88,12,0.04)"  },
    expiry:     { title:`Document Alerts (${expiry_list?.length||0})`,     color:"#dc2626", bg:"rgba(220,38,38,0.04)"  },
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:2.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>Operations Center</Typography>
          <Typography variant="caption" sx={{ color:"#9aa5b4" }}>
            {new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}
          </Typography>
        </Box>
        <Tooltip title="Refresh dashboard">
          <IconButton size="small" onClick={load} sx={{ color:"#6b778c" }}>
            <RefreshIcon sx={{ fontSize:18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2.5}>
        {/* Occupancy ring */}
        <Grid item xs={12} md={3}>
          <Paper elevation={0} sx={{ p:2.5, borderRadius:"16px", border:"1px solid rgba(47,99,173,0.08)", height:"100%", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <OccupancyRing
              pct={stats.occupancy_pct}
              occupied={stats.occupied_slips}   reserved={stats.reserved_slips}
              available={stats.available_slips} maintenance={stats.maintenance_slips}
              total={stats.total_slips}
            />
          </Paper>
        </Grid>

        {/* Stat grid */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={1.75}>
            {/* Slip status */}
            <Grid item xs={4}><StatCard icon={<WavesIcon />}   label="Available"   value={stats.available_slips}   color="#16a34a" bg="rgba(22,163,74,0.08)"   subtitle={`of ${stats.total_slips} total slips`} /></Grid>
            <Grid item xs={4}><StatCard icon={<AnchorIcon />}  label="Reserved"    value={stats.reserved_slips}    color="#2f63ad" bg="rgba(47,99,173,0.08)"   subtitle="Upcoming reservations" /></Grid>
            <Grid item xs={4}><StatCard icon={<BuildIcon />}   label="Maintenance" value={stats.maintenance_slips} color="#6b778c" bg="rgba(107,119,140,0.08)" subtitle="Out of service" /></Grid>

            {/* Today's activity */}
            <Grid item xs={6} sm={3}>
              <StatCard icon={<DirectionsBoatIcon />} label="Arriving Today"  value={stats.arrivals_today}   color="#0891b2" bg="rgba(8,145,178,0.08)"   subtitle="Expected check-ins"  active={panel==="arrivals"}   onClick={() => toggle("arrivals")} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<AnchorIcon />}         label="Departing Today" value={stats.departures_today} color="#7c3aed" bg="rgba(124,58,237,0.08)"  subtitle="Expected check-outs" active={panel==="departures"} onClick={() => toggle("departures")} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<CheckCircleIcon />}    label="On-Site Now"     value={stats.checked_in_now}   color="#ea580c" bg="rgba(234,88,12,0.08)"   subtitle="Currently checked in" active={panel==="checked_in"} onClick={() => toggle("checked_in")} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<HourglassTopIcon />}   label="Waitlist"        value={stats.waitlist_count}   color="#6b778c" bg="rgba(107,119,140,0.08)" subtitle="Pending requests" />
            </Grid>

            {/* Revenue + doc alerts */}
            <Grid item xs={6} sm={4}>
              <StatCard icon={<AttachMoneyIcon />}      label="Revenue Today"  value={fmtMoney(stats.revenue_today)}  color="#15803d" bg="rgba(21,128,61,0.08)"   subtitle="New bookings today" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard icon={<EventAvailableIcon />}   label="Revenue / Month" value={fmtMoney(stats.revenue_month)} color="#0f766e" bg="rgba(15,118,110,0.08)"  subtitle="Month-to-date" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Badge badgeContent={totalAlerts > 0 ? totalAlerts : null} color="error" sx={{ width:"100%", "& .MuiBadge-badge":{ right:14, top:14 } }}>
                <StatCard
                  icon={<WarningAmberIcon />} label="Doc Alerts" value={totalAlerts}
                  color={totalAlerts > 0 ? "#dc2626" : "#16a34a"} bg={totalAlerts > 0 ? "rgba(220,38,38,0.08)" : "rgba(22,163,74,0.08)"}
                  subtitle={totalAlerts > 0 ? "Expired or expiring ≤30 days" : "All documents current"}
                  active={panel==="expiry"} onClick={totalAlerts > 0 ? () => toggle("expiry") : undefined} />
              </Badge>
            </Grid>
          </Grid>
        </Grid>

        {/* ── Expandable detail panel ── */}
        {panel && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ borderRadius:"16px", border:`1px solid ${panelConfig[panel].color}28`, overflow:"hidden" }}>
              <Box sx={{ px:2.5, py:1.5, borderBottom:`1px solid ${panelConfig[panel].color}18`,
                backgroundColor:panelConfig[panel].bg, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142" }}>
                  {panelConfig[panel].title}
                </Typography>
                <IconButton size="small" onClick={() => setPanel(null)}>
                  <Typography sx={{ fontSize:"1.2rem", color:"#6b778c", lineHeight:1, fontWeight:300 }}>×</Typography>
                </IconButton>
              </Box>
              <Box>
                {panel==="arrivals"   && <VesselTable rows={arrivals}    emptyMsg="No arrivals scheduled for today" />}
                {panel==="departures" && <VesselTable rows={departures}  emptyMsg="No departures scheduled for today" />}
                {panel==="checked_in" && <VesselTable rows={checked_in}  emptyMsg="No vessels currently checked in" />}
                {panel==="expiry"     && <ExpiryTable rows={expiry_list} />}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* ── Always-visible expiry alert strip ── */}
        {totalAlerts > 0 && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ px:2.5, py:1.25, borderRadius:"12px", border:"1px solid rgba(220,38,38,0.18)", backgroundColor:"rgba(254,242,242,0.7)", display:"flex", flexWrap:"wrap", gap:1, alignItems:"center" }}>
              <WarningAmberIcon sx={{ fontSize:16, color:"#dc2626" }} />
              <Typography variant="caption" sx={{ fontWeight:700, color:"#dc2626", mr:0.5 }}>Document Alerts:</Typography>
              {stats.insurance_expired     > 0 && <Chip label={`${stats.insurance_expired} Insurance Expired`}         size="small" sx={{ backgroundColor:"rgba(220,38,38,0.12)", color:"#dc2626", fontWeight:700, fontSize:"0.62rem", height:19 }} />}
              {stats.registration_expired  > 0 && <Chip label={`${stats.registration_expired} Reg. Expired`}           size="small" sx={{ backgroundColor:"rgba(220,38,38,0.12)", color:"#dc2626", fontWeight:700, fontSize:"0.62rem", height:19 }} />}
              {stats.insurance_expiring_soon > 0 && <Chip label={`${stats.insurance_expiring_soon} Insurance <30d`}    size="small" sx={{ backgroundColor:"rgba(217,119,6,0.12)", color:"#d97706", fontWeight:700, fontSize:"0.62rem", height:19 }} />}
              {stats.registration_expiring_soon > 0 && <Chip label={`${stats.registration_expiring_soon} Reg. <30d`}  size="small" sx={{ backgroundColor:"rgba(217,119,6,0.12)", color:"#d97706", fontWeight:700, fontSize:"0.62rem", height:19 }} />}
              <Typography variant="caption" onClick={() => toggle("expiry")}
                sx={{ color:"#dc2626", ml:"auto", cursor:"pointer", fontWeight:600, textDecoration:"underline", fontSize:"0.7rem" }}>
                View all →
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default MarinaDashboard;
